import { addMinutes, areIntervalsOverlapping, isSameDay, subHours, subMinutes } from 'date-fns'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { index } from '@last/core'

import sync from '@/sync/service'
import type { Reservation } from '@/types'

export const useReservationsStore = defineStore(
  'reservations',
  () => {
    const reservations = ref<Record<string, Reservation>>({})

    const sortedReservations = computed(() => {
      return Object.values(reservations.value)
        .sort((a, b) => new Date(a.dateTime).valueOf() - new Date(b.dateTime).valueOf())
        .filter(
          reservation =>
            new Date(reservation.dateTime).valueOf() > subHours(new Date(), 4).getTime() &&
            reservation.status !== 'cancelled'
        )
    })

    const searchReservation = computed(() => (tableId: string, date: Date = new Date()) => {
      return sortedReservations.value.find(reservation => {
        const reservationStart = new Date(reservation.dateTime)
        const reservationEnd = new Date(reservation.endTime)

        const hasTab = !!reservation.tabId
        const isAvailable = reservation.status !== 'no-show' && reservation.status !== 'cancelled'
        const rightTable = reservation.tables?.includes(tableId)
        const shouldShow = date >= subMinutes(reservationStart, 90) && date <= reservationEnd

        return !hasTab && rightTable && shouldShow && isAvailable
      })
    })

    const nextTableReservation = computed(() => (tableId: string, currentTime: Date) => {
      return sortedReservations.value.find(reservation => {
        const reservationStart = addMinutes(
          new Date(reservation.dateTime),
          15 // Grace period of 15 minutes
        )

        return (
          !reservation.tabId &&
          reservation.tables?.includes(tableId) &&
          reservationStart > currentTime &&
          isSameDay(reservationStart, currentTime)
        )
      })
    })

    const getBlockedTables = computed(
      () =>
        ({ reservationId, customDate }: { reservationId?: string; customDate?: Date }) => {
          const referenceDate = (() => {
            if (customDate) return customDate
            const reservation = reservationId ? reservations.value[reservationId] : null
            return reservation ? new Date(reservation.dateTime) : new Date()
          })()

          const referenceRangeEnd = addMinutes(referenceDate, 90)
          const referenceInterval = {
            start: referenceDate,
            end: referenceRangeEnd
          }

          return sortedReservations.value
            .filter(({ id, dateTime, endTime, tabId }) => {
              const reservationInterval = { start: dateTime, end: endTime }

              const hasOverlap = areIntervalsOverlapping(referenceInterval, reservationInterval)

              const isCurrentReservation = reservationId ? id === reservationId : false

              return hasOverlap && !isCurrentReservation && !tabId
            })
            .flatMap(reservation => reservation.tables ?? [])
        }
    )

    function createReservation(reservation: Reservation): void {
      sync.record('reservationCreated', { ...reservation, source: 'pos' })
    }

    function editReservation(reservation: Reservation): void {
      sync.record('reservationChanged', { ...reservation, updater: 'pos' })
    }

    function cancelReservation(reservationId: string): void {
      sync.record('reservationCancelled', { reservationId })
    }

    function refreshReservations(inputReservations: Reservation[]): void {
      reservations.value = index(inputReservations, 'id')
    }

    function findReservationByTabId(tabId: string): Reservation | undefined {
      return Object.values(reservations.value).find(r => r.tabId === tabId)
    }

    return {
      reservations,
      sortedReservations,
      searchReservation,
      getBlockedTables,
      nextTableReservation,
      createReservation,
      editReservation,
      cancelReservation,
      refreshReservations,
      findReservationByTabId
    }
  },
  {
    persist: true
  }
)

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useReservationsStore, import.meta.hot))
}
